import { useCallback, useState } from 'react'

interface ReturnType {
  values: Set<string>
  toggle: (id: string) => void
  expand: (ids: string[], reset?: boolean) => void
  reset: () => void
  has: (id: string) => boolean
}

/**
 * Hook to manage which nodes are expanded/collapsed in the job catalog tree
 */
const useUncollapsedNodes = (initialValues: Set<string> = new Set()): ReturnType => {
  const [values, setValues] = useState<Set<string>>(new Set(initialValues))

  const toggle = useCallback((id: string) => {
    setValues((prevValues) => {
      const nextValues = new Set(prevValues)
      if (nextValues.has(id)) {
        nextValues.delete(id)
      } else {
        nextValues.add(id)
      }
      return nextValues
    })
  }, [])

  const expand = useCallback((ids: string[], reset = false) => {
    setValues((prevValues) => {
      const nextValues = reset ? new Set<string>() : new Set(prevValues)
      ids.forEach((id) => nextValues.add(id))
      return nextValues
    })
  }, [])

  const reset = useCallback(() => {
    setValues(new Set())
  }, [])

  const has = useCallback(
    (id: string): boolean => {
      return values.has(id)
    },
    [values]
  )

  return {
    values,
    toggle,
    expand,
    reset,
    has,
  }
}

export default useUncollapsedNodes
